import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext.js';
import { useLanguage } from '../../../context/LanguageContext.js';
import { setAuthToken } from '../../../Utils/authToken.js';
import { markRequireStorePickOnLogin } from '../../../Utils/storePickSession.js';
import { resolvePostLoginPath } from '../../../Utils/postLoginNavigation.js';
import AuthBrand from '../AuthBrand/AuthBrand.jsx';

function AuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const { t } = useLanguage();
    const [status, setStatus] = useState(t('auth.google.processing'));
    const handledTokenRef = useRef(null);

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }

        // checkAuth is a new function on every AuthProvider render, so this effect re-fires while
        // the sign-in is still in flight. Each extra run ends in its own navigate(), which lands
        // after the merchant has already picked a store and throws them back to /select-store.
        if (handledTokenRef.current === token) return;
        handledTokenRef.current = token;

        const isNewAccount = searchParams.get('isNewAccount') === 'true';
        const plan = searchParams.get('plan');
        const interval = searchParams.get('interval') === 'yearly' ? 'yearly' : 'monthly';

        setAuthToken(token, { rememberMe: true });
        markRequireStorePickOnLogin();

        (async () => {
            try {
                await checkAuth();

                if (isNewAccount) {
                    setStatus(t('auth.google.redirectingToPlan'));
                    const billingParams = new URLSearchParams({ interval });
                    if (plan) {
                        billingParams.set('plan', plan);
                    }
                    navigate(`/select-plan?${billingParams.toString()}`, { replace: true });
                    return;
                }

                setStatus(t('auth.google.redirecting'));
                const path = await resolvePostLoginPath({ passwordChangeRequired: false });
                navigate(path, { replace: true });
            } catch {
                navigate('/', { replace: true });
            }
        })();
    }, [checkAuth, navigate, searchParams, t]);

    return (
        <main className="auth-page">
            <AuthBrand corner to="/login" />
            <div className="auth-shell container">
                <div className="auth-card auth-card--narrow">
                    <p className="auth-callback-status">{status}</p>
                </div>
            </div>
        </main>
    );
}

export default AuthCallback;
